import React, { useState, useEffect } from 'react';
import { 
  Plus, Trash2, RefreshCw, Server, Wifi, CheckCircle, XCircle, 
  Edit2, Save, X, Copy, Terminal, Network, Globe, ArrowRight, 
  Play, Pause, TestTube, FileText, Router
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const POPManagement = () => {
  const [pops, setPops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCommandsModal, setShowCommandsModal] = useState(false);
  const [editingPop, setEditingPop] = useState(null);
  const [selectedPop, setSelectedPop] = useState(null);
  const [commands, setCommands] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    vpn_server_password: '',
    ont_subnets: '10.88.88.0/21',
    mikrotik_host: '',
    mikrotik_user: '',
    mikrotik_password: '',
    mikrotik_api_port: '8728',
    mikrotik_use_ssl: false
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchPops = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Use V2 MySQL endpoint
      const response = await fetch(`${API_URL}/api/v2/pop-management`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPops(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching POPs:', error);
      toast.error('Gagal mengambil data POP');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPops();
    // eslint-disable-next-line
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const ontSubnets = formData.ont_subnets
      .split('\n')
      .map(s => s.trim())
      .filter(s => s.length > 0);
    
    const payload = {
      name: formData.name,
      vpn_server_password: formData.vpn_server_password,
      ont_subnets: ontSubnets.length > 0 ? ontSubnets : ['10.88.88.0/21'],
      mikrotik_host: formData.mikrotik_host || null,
      mikrotik_user: formData.mikrotik_user || null,
      mikrotik_password: formData.mikrotik_password || null,
      mikrotik_api_port: formData.mikrotik_api_port ? parseInt(formData.mikrotik_api_port, 10) : 8728,
      mikrotik_use_ssl: formData.mikrotik_use_ssl ? 1 : 0
    };

    try {
      const token = localStorage.getItem('token');
      const url = editingPop 
        // Use V2 MySQL endpoint
        ? `${API_URL}/api/v2/pop-management/${editingPop.id}`
        : `${API_URL}/api/v2/pop-management`;
      
      const response = await fetch(url, {
        method: editingPop ? 'PUT' : 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(editingPop ? 'POP berhasil diupdate' : 'POP berhasil ditambahkan');
        setShowAddModal(false);
        setEditingPop(null);
        resetForm();
        fetchPops();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Gagal menyimpan POP');
      }
    } catch (error) {
      toast.error('Gagal menyimpan POP');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (popId) => {
    if (!window.confirm('Yakin ingin menghapus POP ini? Route di ACS server juga akan dihapus.')) return;

    try {
      const token = localStorage.getItem('token');
      // Use V2 MySQL endpoint
      const response = await fetch(`${API_URL}/api/v2/pop-management/${popId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('POP berhasil dihapus');
        fetchPops();
      } else {
        toast.error('Gagal menghapus POP');
      }
    } catch (error) {
      toast.error('Gagal menghapus POP');
    }
  };

  const handleSetupRoutes = async (popId) => {
    setActionLoading(prev => ({ ...prev, [popId]: 'setup' }));
    
    try {
      const token = localStorage.getItem('token');
      // Use V2 MySQL endpoint
      const response = await fetch(`${API_URL}/api/v2/pop-management/${popId}/setup-routes`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Routes berhasil dikonfigurasi di ACS server');
        fetchPops();
      } else {
        // Show more detailed error message
        const errorMsg = data.data?.message || 'Gagal setup routes';
        toast.error(errorMsg, { duration: 6000 });
      }
    } catch (error) {
      toast.error('Gagal setup routes');
    }
    
    setActionLoading(prev => ({ ...prev, [popId]: null }));
  };

  const handleRemoveRoutes = async (popId) => {
    if (!window.confirm('Yakin ingin menghapus routes untuk POP ini?')) return;
    
    setActionLoading(prev => ({ ...prev, [popId]: 'remove' }));
    
    try {
      const token = localStorage.getItem('token');
      // Use V2 MySQL endpoint
      const response = await fetch(`${API_URL}/api/v2/pop-management/${popId}/remove-routes`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Routes berhasil dihapus dari ACS server');
        fetchPops();
      } else {
        toast.error(data.data?.message || 'Gagal hapus routes');
      }
    } catch (error) {
      toast.error('Gagal hapus routes');
    }
    
    setActionLoading(prev => ({ ...prev, [popId]: null }));
  };


  const handleProvisionNat = async (popId) => {
    setActionLoading(prev => ({ ...prev, [popId]: 'nat' }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v2/pop-management/${popId}/provision-nat`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        const a = data.data?.added?.length || 0;
        const s = data.data?.skipped?.length || 0;
        const f = data.data?.failed?.length || 0;
        toast.success(`NAT provisioned: ${a} added, ${s} skipped, ${f} failed`, { duration: 5000 });
        fetchPops();
      } else {
        toast.error(data.data?.message || 'Gagal provision NAT', { duration: 6000 });
      }
    } catch (e) {
      toast.error('Gagal provision NAT: ' + e.message);
    }
    setActionLoading(prev => ({ ...prev, [popId]: null }));
  };

  const handleRemoveNat = async (popId) => {
    if (!window.confirm('Hapus semua NAT rules Remote ONT yang dibuat untuk POP ini di MikroTik?')) return;
    setActionLoading(prev => ({ ...prev, [popId]: 'remove-nat' }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v2/pop-management/${popId}/remove-nat`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.data?.message || 'NAT rules removed');
        fetchPops();
      } else {
        toast.error(data.data?.message || 'Gagal remove NAT');
      }
    } catch (e) {
      toast.error('Gagal remove NAT: ' + e.message);
    }
    setActionLoading(prev => ({ ...prev, [popId]: null }));
  };

  const handleTestConnection = async (popId) => {
    setActionLoading(prev => ({ ...prev, [popId]: 'test' }));
    
    try {
      const token = localStorage.getItem('token');
      // Use V2 MySQL endpoint
      const response = await fetch(`${API_URL}/api/v2/pop-management/${popId}/test-connection`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success && data.data.results) {
        const results = data.data.results;
        const allOk = results.every(r => r.reachable);
        
        if (allOk) {
          toast.success('Semua koneksi berhasil!');
        } else {
          const failed = results.filter(r => !r.reachable).map(r => r.target).join(', ');
          toast.error(`Koneksi gagal ke: ${failed}`);
        }
        fetchPops();
      } else {
        toast.error(data.data?.message || 'Gagal test koneksi');
      }
    } catch (error) {
      toast.error('Gagal test koneksi');
    }
    
    setActionLoading(prev => ({ ...prev, [popId]: null }));
  };

  const handleShowCommands = async (pop) => {
    setSelectedPop(pop);
    
    try {
      const token = localStorage.getItem('token');
      // Use V2 MySQL endpoint
      const response = await fetch(`${API_URL}/api/v2/pop-management/${pop.id}/mikrotik-commands`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCommands(data.data);
        setShowCommandsModal(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Gagal mengambil commands');
      }
    } catch (error) {
      toast.error('Gagal mengambil commands');
    }
  };

  const copyToClipboard = async (text) => {
    if (!text) {
      toast.error('Tidak ada teks untuk dicopy');
      return;
    }
    
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success('Script berhasil dicopy ke clipboard!');
        return;
      }
      
      // Fallback for HTTP or older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '0';
      textarea.style.top = '0';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      
      const success = document.execCommand('copy');
      document.body.removeChild(textarea);
      
      if (success) {
        toast.success('Script berhasil dicopy ke clipboard!');
      } else {
        toast.error('Gagal copy - silakan select manual dan Ctrl+C');
      }
    } catch (err) {
      console.error('Copy error:', err);
      toast.error('Gagal copy - silakan select manual dan Ctrl+C');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      vpn_server_password: '',
      ont_subnets: '10.88.88.0/21',
      mikrotik_host: '',
      mikrotik_user: '',
      mikrotik_password: '',
      mikrotik_api_port: '8728',
      mikrotik_use_ssl: false
    });
  };

  const openEditModal = (pop) => {
    setEditingPop(pop);
    setFormData({
      name: pop.pop_name || pop.name || '',
      vpn_server_password: pop.vpn_server_password || '',
      ont_subnets: pop.ont_subnets || '10.88.88.0/21',
      mikrotik_host: pop.mikrotik_host || '',
      mikrotik_user: pop.mikrotik_user || '',
      mikrotik_password: pop.mikrotik_password || '',
      mikrotik_api_port: String(pop.mikrotik_api_port || '8728'),
      mikrotik_use_ssl: !!pop.mikrotik_use_ssl
    });
    setShowAddModal(true);
  };

  const getStatusBadge = (pop) => {
    if (pop.remote_access_enabled && pop.last_test_result === 'success') {
      return <Badge className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">Aktif</Badge>;
    } else if (pop.routes_configured) {
      return <Badge className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300">Routes OK</Badge>;
    } else {
      return <Badge className="bg-gray-100 text-gray-600 dark:text-gray-300 dark:bg-slate-800">Belum Setup</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">POP Management</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">Kelola Remote ONT setup untuk setiap MikroTik/POP</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchPops} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={() => { resetForm(); setEditingPop(null); setShowAddModal(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Tambah POP
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 sm:p-4">
        <h4 className="font-medium text-blue-800 dark:text-blue-300 text-sm sm:text-base mb-2">Cara Setup Remote ONT</h4>
        <ol className="text-xs sm:text-sm text-blue-700 dark:text-blue-400 space-y-0.5 sm:space-y-1 list-decimal list-inside">
          <li>Tambah POP baru dengan mengisi <strong>nama</strong> dan <strong>subnet ONT</strong></li>
          <li>Klik <strong>&quot;MikroTik Commands&quot;</strong> - akan generate script VPN kedua ke ACS</li>
          <li><strong>Copy-paste script</strong> ke MikroTik terminal dan tunggu VPN connect</li>
          <li>Klik <strong>&quot;Setup Routes&quot;</strong> untuk menambah route di ACS server</li>
          <li>Klik <strong>&quot;Test&quot;</strong> untuk memastikan koneksi berhasil</li>
        </ol>
        <p className="text-xs text-blue-600 dark:text-blue-400 mt-2 hidden sm:block">
          💡 VPN pertama (Tenant VPN) untuk RADIUS tetap berjalan. Script akan membuat VPN kedua khusus untuk Remote ONT.
        </p>
      </div>

      {/* POP List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-slate-400 dark:text-slate-500" />
        </div>
      ) : pops.length === 0 ? (
        <div className="text-center py-8 text-slate-500 dark:text-slate-400">
          <Server className="w-12 h-12 mx-auto mb-2 text-slate-300" />
          <p>Belum ada POP yang dikonfigurasi</p>
          <p className="text-sm">Klik &quot;Tambah POP&quot; untuk memulai</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {pops.map((pop) => (
            <div key={pop.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3 sm:p-4 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Router className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600 dark:text-slate-300" />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                      <h4 className="font-medium text-sm sm:text-base text-slate-800 dark:text-slate-100">{pop.name}</h4>
                      {getStatusBadge(pop)}
                    </div>
                    <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1 space-y-0.5">
                      <p><span className="text-slate-400 dark:text-slate-500">L2TP Peer:</span> {pop.l2tp_peer_ip || '-'}</p>
                      <p><span className="text-slate-400 dark:text-slate-500">ONT Subnets:</span> {(pop.ont_subnets || []).join(', ') || '-'}</p>
                      <p><span className="text-slate-400 dark:text-slate-500">PPPoE Subnets:</span> {(pop.pppoe_subnets || []).join(', ') || '-'}</p>
                      {pop.vpn_server_host && (
                        <p><span className="text-slate-400 dark:text-slate-500">VPN Server:</span> <span className="text-blue-600 dark:text-blue-400">{pop.vpn_server_host}</span></p>
                      )}
                      {pop.last_tested && (
                        <p className="text-xs text-slate-400 dark:text-slate-500">
                          Last test: {new Date(pop.last_tested).toLocaleString()} - 
                          {pop.last_test_result === 'success' ? (
                            <span className="text-green-600 ml-1 dark:text-green-400">✓ OK</span>
                          ) : pop.last_test_result === 'partial' ? (
                            <span className="text-yellow-600 ml-1 dark:text-yellow-400">⚠ Partial</span>
                          ) : (
                            <span className="text-red-600 ml-1 dark:text-red-400">✗ Failed</span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-1 sm:gap-2">
                  <Button 
                    variant="outline"
                    size="sm"
                    className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                    onClick={() => handleShowCommands(pop)}
                    title="Lihat MikroTik Commands"
                  >
                    <Terminal className="w-4 h-4" />
                  </Button>
                  
                  {!pop.routes_configured ? (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleSetupRoutes(pop.id)}
                      disabled={actionLoading[pop.id] === 'setup'}
                      className="text-green-600 dark:text-green-400 border-green-200 dark:border-green-800 hover:bg-green-50 dark:hover:bg-green-900/30"
                      title="Setup Routes di ACS Server"
                    >
                      {actionLoading[pop.id] === 'setup' ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                    </Button>
                  ) : (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleRemoveRoutes(pop.id)}
                      disabled={actionLoading[pop.id] === 'remove'}
                      className="text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/30"
                      title="Hapus Routes dari ACS Server"
                    >
                      {actionLoading[pop.id] === 'remove' ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Pause className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                  
                  {!pop.nat_provisioned ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleProvisionNat(pop.id)}
                      disabled={actionLoading[pop.id] === 'nat'}
                      className="text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                      title="Auto-Provision NAT Rule di MikroTik (untuk Huawei/ONT yang block non-LAN)"
                      data-testid={`pop-provision-nat-btn-${pop.id}`}
                    >
                      {actionLoading[pop.id] === 'nat' ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Wifi className="w-4 h-4" />
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveNat(pop.id)}
                      disabled={actionLoading[pop.id] === 'remove-nat'}
                      className="text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-800 hover:bg-orange-50 dark:hover:bg-orange-900/30"
                      title="Hapus NAT Rules di MikroTik"
                      data-testid={`pop-remove-nat-btn-${pop.id}`}
                    >
                      {actionLoading[pop.id] === 'remove-nat' ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <XCircle className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                  
                  <Button 
                    variant="outline"
                    size="sm"
                    className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                    onClick={() => handleTestConnection(pop.id)}
                    disabled={actionLoading[pop.id] === 'test'}
                    className="text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                    title="Test Koneksi"
                  >
                    {actionLoading[pop.id] === 'test' ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <TestTube className="w-4 h-4" />
                    )}
                  </Button>
                  
                  <Button 
                    variant="outline"
                    size="sm"
                    className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                    onClick={() => openEditModal(pop)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  
                  <Button 
                    variant="outline"
                    size="sm"
                    className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                    onClick={() => handleDelete(pop.id)}
                    className="text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/30"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal - SIMPLE */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingPop ? 'Edit POP' : 'Tambah POP Baru'}</DialogTitle>
            <DialogDescription>
              Buat POP untuk konek L2TP ke Remote Server
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nama POP */}
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Nama POP *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Contoh: POP Depok"
                required
              />
            </div>
            
            {/* Password VPN */}
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Password VPN *</label>
              <Input
                value={formData.vpn_server_password}
                onChange={(e) => setFormData({ ...formData, vpn_server_password: e.target.value })}
                placeholder="Password untuk koneksi L2TP"
                required
              />
              <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">Username akan di-generate otomatis</p>
            </div>
            
            {/* Subnet ONT */}
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Subnet Pelanggan</label>
              <Input
                value={formData.ont_subnets}
                onChange={(e) => setFormData({ ...formData, ont_subnets: e.target.value })}
                placeholder="10.88.88.0/21"
              />
              <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">Subnet IP pelanggan (PPPoE/ONT)</p>
            </div>

            {/* MikroTik Credentials (untuk Auto-Provision NAT) */}
            <details className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 bg-slate-50 dark:bg-slate-800/50">
              <summary className="text-sm font-medium text-slate-700 dark:text-slate-200 cursor-pointer select-none">
                Kredensial MikroTik (opsional, untuk Auto-Provision NAT)
              </summary>
              <div className="mt-3 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div className="md:col-span-2">
                    <label className="text-xs text-slate-600 dark:text-slate-400">Host / IP</label>
                    <Input
                      value={formData.mikrotik_host}
                      onChange={(e) => setFormData({ ...formData, mikrotik_host: e.target.value })}
                      placeholder="10.10.10.104"
                      data-testid="pop-mikrotik-host-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 dark:text-slate-400">API Port</label>
                    <Input
                      type="number"
                      value={formData.mikrotik_api_port}
                      onChange={(e) => setFormData({ ...formData, mikrotik_api_port: e.target.value })}
                      placeholder="8728"
                      data-testid="pop-mikrotik-port-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-slate-600 dark:text-slate-400">Username</label>
                    <Input
                      value={formData.mikrotik_user}
                      onChange={(e) => setFormData({ ...formData, mikrotik_user: e.target.value })}
                      placeholder="admin"
                      autoComplete="off"
                      data-testid="pop-mikrotik-user-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 dark:text-slate-400">Password</label>
                    <Input
                      type="password"
                      value={formData.mikrotik_password}
                      onChange={(e) => setFormData({ ...formData, mikrotik_password: e.target.value })}
                      placeholder="••••••"
                      autoComplete="new-password"
                      data-testid="pop-mikrotik-pass-input"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.mikrotik_use_ssl}
                    onChange={(e) => setFormData({ ...formData, mikrotik_use_ssl: e.target.checked, mikrotik_api_port: e.target.checked && formData.mikrotik_api_port === '8728' ? '8729' : formData.mikrotik_api_port })}
                    className="rounded"
                    data-testid="pop-mikrotik-ssl-checkbox"
                  />
                  Pakai SSL (RouterOS v7+ port 8729)
                </label>
                <p className="text-xs text-slate-400 dark:text-slate-500">
                  Diperlukan jika ingin pakai tombol <strong>Auto-Provision NAT</strong>. Tidak wajib.
                </p>
              </div>
            </details>

            {/* Info */}
            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <p className="text-xs text-blue-700 dark:text-blue-400">
                <strong>Server L2TP:</strong> 103.179.252.150<br/>
                <strong>Setelah simpan:</strong> Klik "MikroTik Script" untuk dapat script koneksi
              </p>
            </div>
            
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => { setShowAddModal(false); resetForm(); }}>
                Batal
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Menyimpan...' : (editingPop ? 'Update' : 'Simpan')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* MikroTik Commands Modal */}
      <Dialog open={showCommandsModal} onOpenChange={setShowCommandsModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>MikroTik Commands - {selectedPop?.name}</DialogTitle>
            <DialogDescription>
              Copy dan jalankan script ini di MikroTik terminal untuk membuat VPN kedua ke ACS
            </DialogDescription>
          </DialogHeader>
          
          {commands && (
            <div className="space-y-4">
              {/* VPN Credentials Info */}
              {(commands.l2tp_vpn_username || commands.acs_vpn_username) && (
                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">Info Koneksi VPN ke Remote Server</h4>
                  <div className="text-xs text-blue-700 space-y-1 dark:text-blue-300">
                    <p><strong>Server:</strong> {commands.l2tp_server || commands.acs_server}</p>
                    <p><strong>Username:</strong> {commands.l2tp_vpn_username || commands.acs_vpn_username}</p>
                    <p><strong>Password:</strong> <span className="font-mono bg-white px-2 py-0.5 rounded dark:bg-slate-800">{commands.l2tp_vpn_password || commands.acs_vpn_password}</span></p>
                    <p><strong>Interface:</strong> {commands.l2tp_vpn_interface || commands.acs_vpn_interface}</p>
                  </div>
                </div>
              )}
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Full Script</label>
                  <Button 
                    type="button"
                    variant="outline"
                    size="sm"
                    className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (commands && commands.full_script) {
                        copyToClipboard(commands.full_script);
                      } else {
                        toast.error('Script tidak tersedia');
                      }
                    }}
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                </div>
                <pre className="bg-slate-900 text-green-400 p-4 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap max-h-80 overflow-y-auto">
                  {commands.full_script}
                </pre>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 dark:bg-green-900/30">
                <p className="text-sm text-green-800">
                  <strong>✓ Aman dijalankan berulang:</strong> Script sudah include perintah remove untuk mencegah duplikasi.
                </p>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 dark:bg-yellow-900/30">
                <p className="text-sm text-yellow-800">
                  <strong>Langkah selanjutnya:</strong>
                </p>
                <ol className="text-sm text-yellow-700 list-decimal list-inside mt-1 space-y-1 dark:text-yellow-300">
                  <li>Copy script dan paste di <strong>MikroTik Terminal</strong></li>
                  <li>Tunggu beberapa detik sampai VPN <strong>CONNECTED</strong></li>
                  <li>Kembali ke halaman ini dan klik 
                    <span className="inline-flex items-center mx-1 px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs dark:bg-green-900/40 dark:text-green-300">
                      <Play className="w-3 h-3 mr-1" /> Setup Routes
                    </span>
                  </li>
                </ol>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommandsModal(false)}>
              Tutup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POPManagement;
